import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ProductService from '../../api/ProductService';
import { productManagementActions } from '../../store/product-management-slice';
import { Product } from "../../types/product";
import { Meta } from "../../types/response";
import { CustomButton } from '../CustomButton/CustomButton';

import styles from './addProductModalTabs.module.css';
import { AssignListModal } from './AssignListModal';
import { Column, Table } from './Table';
export const ZubehorTab = () => {
    const dispatch = useDispatch();
    const product = useSelector((state: any) => state.productManagement.product);

    const [isOpen, setIsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts] = useState<Product[]>([]);
    const [meta, setMeta] = useState<Meta>({
        total: 0,
        count: 0,
        perPage: 0,
        currentPage: 1,
        totalPages: 1
    });
    const hasMore = currentPage < meta.totalPages;;

    const handleLoadMore = () => {
        if (!hasMore || isLoading) return;
        setCurrentPage((prev) => prev + 1);
    };


    const columns: Column<Product>[] = [
        {
            key: "name",
            label: "Material",
        },
        {
            key: "referenceNumber",
            label: "Art.-Nr.",
        },
        {
            key: "salesUnit",
            label: "Einheit",
            render: (value: any) => value?.name || '-'
        },
    ];

    const emptyTable = {
        title: "Keine Materialien verbunden",
        text: "Fügen Sie Materialien hinzu, um sie hier anzuzeigen"
    };

    const relatedProductDetails = useSelector((state: any) => state.productManagement.relatedProductDetails);

    // The picker pages its list in, so a connected material is looked up in what it has loaded
    // and in the details the product came with.
    const tableData = useMemo(() => {
        const known = new Map<string, Product>();
        relatedProductDetails.forEach((el: Product) => known.set(el.id, el));
        products.forEach((el: Product) => known.set(el.id, el));

        return product.relatedProducts
            .map((id: string) => known.get(id))
            .filter((el: Product | undefined): el is Product => Boolean(el));
    }, [products, relatedProductDetails, product.relatedProducts]);

    const selectableProducts = useMemo(() => products.filter((el: Product) => el.id !== product.id), [products, product.id]);

    const getProducts = useCallback(async (loadMore = true) => {
        try {
            setIsLoading(true);
            const response = await ProductService.GetProducts({ perPage: 20, page: loadMore ? currentPage : 1, search: searchTerm });
            const filterIsHidden = response.data.filter((el: Product) => !el.isHidden);
            setProducts((prev) => loadMore ? [...prev, ...filterIsHidden] : filterIsHidden);
            setMeta(response.meta);
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
        finally {
            setIsLoading(false);
        }

    }, [currentPage, searchTerm]);

    const handleSaveProducts = async (ids: string[]) => {
        dispatch(productManagementActions.setSelectedProductIds(ids));
    }

    useEffect(() => {
        getProducts();
    }, [currentPage])

    useEffect(() => {
        const debouncedSearch = debounce(async () => {
            getProducts(false);
        }, 1000);
        debouncedSearch();
        return () => debouncedSearch.cancel();
    }, [searchTerm]);

    return <div className={styles["tab-zubehor"]}>
        <div className={styles["tab-zubehor-header"]}>
            <p>Verbundene Materialien für <strong>{product.name}</strong></p>
            <CustomButton onClick={() => setIsOpen(true)} color='lightGreenNew' icon={product.relatedProducts.length ? 'edit' : 'add'} iconPosition='left' customClass={styles["zubehor-tab-bearbeiten-button"]}>{product.relatedProducts.length ? 'Bearbeiten' : 'Hinzufügen'}</CustomButton>
        </div>
        <Table columns={columns} data={tableData} empty={emptyTable} />
        <AssignListModal<Product, string>
            isOpen={isOpen}
            title="Artikel auswählen"
            onClose={() => setIsOpen(false)}
            data={selectableProducts}
            selectedLabel={(count) => `${count} Artikel ausgewählt`}
            setSearch={setSearchTerm}
            search={searchTerm}
            selectedItems={product.relatedProducts}
            getId={(product) => product.id}
            onSave={handleSaveProducts}
            isSelected={(product, selectedItems) =>
                selectedItems.includes(product.id)
            }
            onToggle={(product, enabled, selectedItems) => {
                if (enabled) {
                    return selectedItems.includes(product.id)
                        ? selectedItems
                        : [...selectedItems, product.id];
                }

                return selectedItems.filter(
                    (id) => id !== product.id
                );
            }}

            renderInfo={(product) => (
                <div>
                    <p className={styles["product-name"]}>{product.name}</p>
                    <p className={styles["product-reference-number"]}>
                        Artikel nr: {product.referenceNumber ?? "-"}
                        {product.salesUnit?.name ? `, ${product.salesUnit.name}` : ""}
                    </p>
                </div>
            )}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            isLoading={isLoading}
        />
    </div>;
};